import Vue, { CreateElement } from '@src/vue'
import { Component } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'

import { track } from '@src/util/functions'
import { Subtitle1 } from '@src/elements/typography/typography'
import { Input } from '@src/elements/input/input'
import { Textarea } from '@src/elements/input/textarea'
import { Checkbox } from '@src/elements/input/checkbox'
import { ContentBox } from '@src/elements/content-box/content-box'
import { Button } from '@src/elements/button/button'
import { retentionFlags } from '@src/util/constants'
import { Filter } from '@src/routes/content/components/filter'
import { Spacer } from '@src/elements/spacer/spacer'
import { Message } from '@src/data/message'
import ApiClient from '@src/lib/api_client'
import { API } from '@src/lib/api_path'
import { RecipientsInput } from '@src/routes/messaging/recipients-input'
import Member, { MemberState } from '@src/data/member'
import { ConfirmRecipients } from '@src/routes/messaging/confirm-recipients'
import { MemberService } from '@src/service/member'

import './compose-message.sass'

type MembersResponse = { members: Member[] }

@Component
export class ComposeMessage extends Vue {

  private message: Message = new Message()

  private allMembers: Member[] = []

  private selectedAlerts: Set<string> = new Set<string>([])

  private apiClient: ApiClient = new ApiClient()
  private showConfirmSend: boolean = false

  private recipients: (Member | string)[] = []

  constructor () {
    super()
    track('/messaging')
  }

  created () {
    return this.loadMembers()
  }

  updated () {
    document.title = `${this.$t('PIXFORMANCE_PLATFORM')} - ${this.$t('PAGE.MESSAGING.TITLE')}`
  }

  render (h: CreateElement) {

    const membersToBeConfirmed: Member[] = this.composeRecipients()

    const disableSend = (!this.message.emailNotification && !this.message.pushNotification) || membersToBeConfirmed.length === 0 || !this.message.subject || !this.message.body

    return <ContentBox class='messaging-wrapper'>
      <div slot='content'>
        <div class='row filter-row'>
          <Subtitle1 class='title'>{this.$t('PAGE.MESSAGING.ALERTS_FILTER_DESCRIPTION')}</Subtitle1>
          <Filter cssClass='retention-flags'
                  tag={'retention_flags'}
                  counter={retentionFlags.map((flag) => this.allMembers.filter((m) => new MemberService().filterByAlerts(m, new Set([flag]), false)).length)}
                  filterList={retentionFlags}
                  placeholder={this.$t('PAGE.MESSAGING.FILTER_BY_ALERTS')}
                  translation={'PAGE.MEMBERS.LIST.HEADERS.RETENTION_FLAGS.FILTERS.'}
                  selectedFilters={this.selectedAlerts}
                  filterCallback={this.selectAlertFilter}
                  clearAllCallback={this.clearAlertFilters} />
        </div>
        <Spacer />

        <RecipientsInput allMembers={this.allMembers} recipients={this.recipients}
                         alertRemoved={this.selectAlertFilter} memberAdded={this.onMemberAdded} memberRemoved={this.onMemberRemoved} />
        <Spacer />
        <Input label={this.$t('PAGE.MESSAGING.SUBJECT')} ref='subject' value={this.message.subject} valueChanged={value => this.message.subject = value} />
        <Spacer />
        <Textarea label={this.$t('PAGE.MESSAGING.BODY')} ref='body' value={this.message.body} keyup={value => this.message.body = value} />
        <Spacer />

        <Subtitle1 class='title'>{this.$t('PAGE.MESSAGING.SEND_VIA')}</Subtitle1>
        <Spacer />
        <div class='row checkboxes-row'>
          <Checkbox text={this.$t('EMAIL')} ref='email' value={this.message.emailNotification} change={() => this.message.emailNotification = !this.message.emailNotification} />
          <Checkbox text={this.$t('PAGE.MESSAGING.PUSH_NOTIFICATION')} value={this.message.pushNotification} ref='pushNotification'
                    change={() => this.message.pushNotification = !this.message.pushNotification} />
        </div>

        {this.showConfirmSend ? <ConfirmRecipients members={membersToBeConfirmed} message={this.message} closeCallback={this.closeCallback} /> : null}
      </div>

      <div slot='bottom'>
        <Button label={this.$t('PAGE.MESSAGING.SEND')} click={this.attemptSend} type='elevated' ref='send' disabled={disableSend} />
      </div>
    </ContentBox>
  }

  attemptSend () {
    this.showConfirmSend = true
  }

  closeCallback (sent?: boolean) {
    this.showConfirmSend = false
    if (sent) {
      this.message = new Message()
      this.recipients = []
      this.selectedAlerts = new Set<string>([])
    }
  }

  selectAlertFilter (value: string) {
    if (this.selectedAlerts.has(value)) {
      this.selectedAlerts.delete(value)
      let index = this.recipients.findIndex(alert => alert === value)
      this.recipients.splice(index, 1)
    } else {
      this.selectedAlerts.add(value)
      this.recipients.push(value)
    }
    this.$forceUpdate()
  }

  clearAlertFilters () {
    this.selectedAlerts = new Set([])
    let recipientsWithoutAlerts: Member[] = []
    this.recipients.forEach(recipient => {
      if (typeof recipient !== 'string') {
        recipientsWithoutAlerts.push(recipient)
      }
    })
    this.recipients = recipientsWithoutAlerts
  }

  onMemberAdded (member: Member) {
    this.recipients.push(member)
  }

  onMemberRemoved (member: Member) {
    let index = this.recipients.findIndex(recipient => recipient === member)
    this.recipients.splice(index, 1)
  }

  composeRecipients (): Member[] {
    let selectedMembers: Member[] = []

    this.recipients.forEach(recipient => {
      if (typeof recipient !== 'string' && !selectedMembers.includes(recipient)) {
        selectedMembers.push(recipient)
      }
    })

    return [
      ...selectedMembers,
      ...this.allMembers.filter((member) => !selectedMembers.includes(member) && new MemberService().filterByAlerts(member, this.selectedAlerts || new Set([]), false))
    ]
  }

  private loadMembers () {
    return this.apiClient.get<MembersResponse>(API.TRAINERS_API.MEMBERS)
      .then(response => this.allMembers = response.members
        .filter(member => member.state === MemberState.PUBLISHED)
        .map((attrs) => new Member(camelcaseKeys(attrs))))
      .catch(error => console.error('failed to retrieve members', error))
  }
}
